import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface DropdownOption {
  id: string;
  name: string;
  description?: string;
  colorCode?: string;
  isActive?: boolean;
  displayOrder?: number;
  type: 'category' | 'deviceModel' | 'customerType' | 'quickMessage' | 'parts';
  // Quick message specific fields
  category?: string;
  language?: string;
  // Parts specific fields
  part_number?: string;
  item_name?: string;
}

interface DropdownOptionsManagerProps {
  onOptionsUpdate?: () => void;
}

const DropdownOptionsManager: React.FC<DropdownOptionsManagerProps> = ({ onOptionsUpdate }) => {
  const [options, setOptions] = useState<{
    categories: DropdownOption[];
    deviceModels: DropdownOption[];
    customerTypes: DropdownOption[];
    quickMessages: DropdownOption[];
    parts: DropdownOption[];
  }>({
    categories: [],
    deviceModels: [],
    customerTypes: [],
    quickMessages: [],
    parts: []
  });
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'categories' | 'deviceModels' | 'customerTypes' | 'quickMessages' | 'parts'>('categories');
  const [editingItem, setEditingItem] = useState<DropdownOption | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadOptions();
  }, []);

  const loadOptions = async () => {
    try {
      setLoading(true);
      setMessage(''); // Clear any previous error messages
      console.log('DropdownOptionsManager: Starting to load options...');
      
      // Load dropdown options first (this is the essential functionality)
      try {
        const dropdownData = await apiService.getDropdownOptions();
        console.log('DropdownOptionsManager: Successfully loaded dropdown data:', dropdownData);
        
        // Set dropdown options first
        setOptions(prev => ({
          ...prev,
          categories: dropdownData.categories.map((c: any) => ({ ...c, type: 'category' as const })),
          deviceModels: dropdownData.deviceModels.map((d: any) => ({ ...d, type: 'deviceModel' as const })),
          customerTypes: dropdownData.customerTypes.map((c: any) => ({ ...c, type: 'customerType' as const }))
        }));
      } catch (error) {
        console.error('DropdownOptionsManager: Error loading dropdown options:', error);
        const errorMessage = (error as Error).message;
        
        if (errorMessage.includes('Authentication failed') || errorMessage.includes('401')) {
          setMessage('Authentication failed. Please log in again.');
        } else if (errorMessage.includes('403') || errorMessage.includes('Admin access required')) {
          setMessage('Access denied. Only administrators can manage dropdown options.');
        } else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('Network')) {
          setMessage('Network error. Please check if the backend server is running.');
        } else {
          setMessage(`Failed to load dropdown options: ${errorMessage}`);
        }
        return; // Exit early if dropdown options fail
      }
      
      // Load quick messages separately (this is optional functionality)
      try {
        const quickMessagesData = await apiService.getQuickMessagesAdmin();
        console.log('DropdownOptionsManager: Successfully loaded quick messages data:', quickMessagesData);
        
        setOptions(prev => ({
          ...prev,
          quickMessages: quickMessagesData.map(q => ({ 
            ...q, 
            type: 'quickMessage' as const,
            name: q.title,
            description: `${q.content.substring(0, 100)}${q.content.length > 100 ? '...' : ''} (${q.language.toUpperCase()}, ${q.category})`
          }))
        }));
      } catch (quickMessagesError) {
        console.warn('DropdownOptionsManager: Failed to load quick messages (this is optional):', quickMessagesError);
        // Don't set an error message for quick messages failure, as it's optional
        // Just log the warning and continue with the other dropdown options
        setOptions(prev => ({
          ...prev,
          quickMessages: [] // Set empty array so the tab still works
        }));
      }

      // Load parts separately (this is optional functionality)
      try {
        const partsData = await apiService.getPartsAdmin();
        console.log('DropdownOptionsManager: Successfully loaded parts data:', partsData);
        
        setOptions(prev => ({
          ...prev,
          parts: partsData.map(p => ({ 
            ...p, 
            type: 'parts' as const,
            id: p.part_number, // Use part_number as the ID
            name: p.part_number,
            description: `${p.item_name}${p.category ? ` (${p.category})` : ''}`
          }))
        }));
      } catch (partsError) {
        console.warn('DropdownOptionsManager: Failed to load parts (this is optional):', partsError);
        // Don't set an error message for parts failure, as it's optional
        // Just log the warning and continue with the other dropdown options
        setOptions(prev => ({
          ...prev,
          parts: [] // Set empty array so the tab still works
        }));
      }
      
      console.log('DropdownOptionsManager: Options loading completed');
    } catch (error) {
      console.error('DropdownOptionsManager: Unexpected error in loadOptions:', error);
      setMessage(`Unexpected error: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadCategoriesDirectly = async () => {
    try {
      console.log('Testing direct categories API call...');
      const data = await apiService.getCategories();
      console.log('Categories loaded successfully:', data);
      
      setOptions(prev => ({
        ...prev,
        categories: data.map(c => ({ ...c, type: 'category' as const }))
      }));
      
      setMessage('Categories loaded successfully using direct API call!');
    } catch (error) {
      console.error('Direct categories call failed:', error);
      setMessage(`Direct categories call failed: ${(error as Error).message}`);
    }
  };

  const handleCreate = async (data: Partial<DropdownOption>) => {
    try {
      switch (activeTab) {
        case 'categories':
          await apiService.createCategory({
            name: data.name!,
            description: data.description || '',
            colorCode: data.colorCode || '#6c757d'
          });
          break;
        case 'deviceModels':
          await apiService.createDeviceModel({
            name: data.name!,
            description: data.description || '',
            isActive: data.isActive ?? true,
            displayOrder: data.displayOrder || 0
          });
          break;
        case 'customerTypes':
          await apiService.createCustomerType({
            name: data.name!,
            description: data.description || '',
            colorCode: data.colorCode || '#6c757d',
            isActive: data.isActive ?? true,
            displayOrder: data.displayOrder || 0
          });
          break;
        case 'quickMessages':
          await apiService.createQuickMessage({
            title: data.name!,
            content: data.description || '',
            category: data.category || 'general',
            language: data.language || 'en',
            isActive: data.isActive ?? true,
            displayOrder: data.displayOrder || 0
          });
          break;
        case 'parts':
          await apiService.createPart({
            part_number: data.name!,
            item_name: data.description || data.name!,
            category: data.category || 'Other',
            description: data.description || ''
          });
          break;
      }

      setMessage(`${data.name} created successfully!`);
      setShowAddForm(false);
      loadOptions();
      onOptionsUpdate?.();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.message || 'Failed to create item');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleUpdate = async (id: string, data: Partial<DropdownOption>) => {
    try {
      switch (activeTab) {
        case 'categories':
          await apiService.updateCategory(id, {
            name: data.name,
            description: data.description,
            colorCode: data.colorCode
          });
          break;
        case 'deviceModels':
          await apiService.updateDeviceModel(id, {
            name: data.name,
            description: data.description,
            isActive: data.isActive,
            displayOrder: data.displayOrder
          });
          break;
        case 'customerTypes':
          await apiService.updateCustomerType(id, {
            name: data.name,
            description: data.description,
            colorCode: data.colorCode,
            isActive: data.isActive,
            displayOrder: data.displayOrder
          });
          break;
        case 'quickMessages':
          await apiService.updateQuickMessage(id, {
            title: data.name,
            content: data.description,
            category: data.category,
            language: data.language,
            isActive: data.isActive,
            displayOrder: data.displayOrder
          });
          break;
        case 'parts':
          await apiService.updatePart(id, {
            item_name: data.description || data.name,
            category: data.category,
            description: data.description
          });
          break;
      }

      setMessage(`${data.name} updated successfully!`);
      setEditingItem(null);
      loadOptions();
      onOptionsUpdate?.();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.message || 'Failed to update item');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      switch (activeTab) {
        case 'categories':
          await apiService.deleteCategory(id);
          break;
        case 'deviceModels':
          await apiService.deleteDeviceModel(id);
          break;
        case 'customerTypes':
          await apiService.deleteCustomerType(id);
          break;
        case 'quickMessages':
          await apiService.deleteQuickMessage(id);
          break;
        case 'parts':
          await apiService.deletePart(id);
          break;
      }

      setMessage(`${name} deleted successfully!`);
      loadOptions();
      onOptionsUpdate?.();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error('Delete error:', error);
      
      // Handle specific error cases
      if (error.message?.includes('Category not found') || error.message?.includes('404')) {
        setMessage(`${name} was already deleted or doesn't exist. Refreshing list...`);
        loadOptions(); // Refresh the list to sync with database
      } else if (error.message?.includes('CATEGORY_IN_USE')) {
        setMessage(`Cannot delete ${name}: it's being used by existing tickets`);
      } else {
        setMessage(error.message || 'Failed to delete item');
      }
      
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const getCurrentOptions = () => {
    return options[activeTab] || [];
  };

  const getTabLabel = (tab: string) => {
    switch (tab) {
      case 'categories': return 'Ticket Categories';
      case 'deviceModels': return 'Device Models';
      case 'customerTypes': return 'Customer Types';
      case 'quickMessages': return 'Quick Messages';
      case 'parts': return 'Parts';
      default: return tab;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Dropdown Options Management
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Configure the options available in dropdown menus throughout the system
        </p>
      </div>

      {message && (
        <div className={`mb-4 p-4 rounded-md ${
          message.includes('success') 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
        }`}>
          {message}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {(['categories', 'deviceModels', 'customerTypes', 'quickMessages', 'parts'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setEditingItem(null);
                setShowAddForm(false);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {getTabLabel(tab)}
            </button>
          ))}
        </nav>
      </div>

      {/* Add Button */}
      <div className="mb-4 flex justify-between items-center">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">
          {getTabLabel(activeTab)}
        </h4>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
        >
          Add New {getTabLabel(activeTab).slice(0, -1)}
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <AddEditForm
          type={activeTab}
          onSave={handleCreate}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {/* Options List */}
      <div className="space-y-3">
        {getCurrentOptions().map((option) => (
          <div
            key={option.id}
            className="border border-gray-200 dark:border-gray-600 rounded-lg p-4"
          >
            {editingItem?.id === option.id ? (
              <AddEditForm
                type={activeTab}
                initialData={editingItem}
                onSave={(data) => handleUpdate(option.id, data)}
                onCancel={() => setEditingItem(null)}
              />
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                        {option.name}
                      </h5>
                      {option.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {option.description}
                        </p>
                      )}
                    </div>
                    {option.colorCode && (
                      <div
                        className="w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600"
                        style={{ backgroundColor: option.colorCode }}
                      />
                    )}
                    {option.isActive !== undefined && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        option.isActive 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {option.isActive ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingItem(option)}
                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(option.id, option.name)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {getCurrentOptions().length === 0 && !message && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No {getTabLabel(activeTab).toLowerCase()} found. Add one to get started.
        </div>
      )}

      {message && getCurrentOptions().length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500 dark:text-gray-400 mb-4">
            Unable to load {getTabLabel(activeTab).toLowerCase()}.
          </div>
          <div className="space-x-2">
            <button
              onClick={loadOptions}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
            >
              Retry
            </button>
            <button
              onClick={loadCategoriesDirectly}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
            >
              Test Categories API
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Add/Edit Form Component
interface AddEditFormProps {
  type: 'categories' | 'deviceModels' | 'customerTypes' | 'quickMessages' | 'parts';
  initialData?: DropdownOption;
  onSave: (data: Partial<DropdownOption>) => void;
  onCancel: () => void;
}

const AddEditForm: React.FC<AddEditFormProps> = ({ type, initialData, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    colorCode: initialData?.colorCode || '#6c757d',
    isActive: initialData?.isActive ?? true,
    displayOrder: initialData?.displayOrder || 0,
    category: initialData?.category || 'general',
    language: initialData?.language || 'en',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    onSave(formData);
  };

  const showColorPicker = type === 'categories' || type === 'customerTypes';
  const showActiveToggle = type === 'deviceModels' || type === 'customerTypes' || type === 'quickMessages';
  const showDisplayOrder = type === 'deviceModels' || type === 'customerTypes' || type === 'quickMessages';
  const isQuickMessage = type === 'quickMessages';
  const isParts = type === 'parts';

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={isQuickMessage || isParts ? 'md:col-span-2' : ''}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {isQuickMessage ? 'Title *' : isParts ? 'Part Number *' : 'Name *'}
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            required
            placeholder={isParts ? 'Enter part number (e.g., SV1106)' : ''}
          />
        </div>
        <div className={isQuickMessage || isParts ? 'md:col-span-2' : ''}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {isQuickMessage ? 'Message Content *' : isParts ? 'Item Name *' : 'Description'}
          </label>
          {isQuickMessage ? (
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              rows={3}
              required
              placeholder="Enter the message content that will be sent to customers..."
            />
          ) : (
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              required={isParts}
              placeholder={isParts ? 'Enter item name/description' : ''}
            />
          )}
        </div>
        {isQuickMessage && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                required
              >
                <option value="welcome">Welcome</option>
                <option value="troubleshooting">Troubleshooting</option>
                <option value="setup">Device Setup</option>
                <option value="connection">Connection</option>
                <option value="escalation">Escalation</option>
                <option value="closure">Closure</option>
                <option value="general">General</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Language *
              </label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                required
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
                <option value="pt">Português</option>
              </select>
            </div>
          </>
        )}
        {isParts && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              required
            >
              <option value="Service">Service</option>
              <option value="Software">Software</option>
              <option value="Raw Material">Raw Material</option>
              <option value="Supply Item">Supply Item</option>
              <option value="Assembly Item">Assembly Item</option>
              <option value="Other">Other</option>
            </select>
          </div>
        )}
        {showColorPicker && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={formData.colorCode}
                onChange={(e) => setFormData({ ...formData, colorCode: e.target.value })}
                className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-md"
              />
              <input
                type="text"
                value={formData.colorCode}
                onChange={(e) => setFormData({ ...formData, colorCode: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        )}
        {showDisplayOrder && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Display Order
            </label>
            <input
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
        )}
        {showActiveToggle && (
          <div className="flex items-center">
            <input
              id="isActive"
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 dark:text-white">
              Active
            </label>
          </div>
        )}
      </div>
      <div className="mt-4 flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
        >
          {initialData ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default DropdownOptionsManager; 